/**
 * Word-level diff for the history view, via longest common subsequence. Wiki revisions are
 * small; O(n·m) on words is comfortably fast at this scale.
 */

export type DiffPart = { kind: "same" | "added" | "removed"; text: string };

export function diffWords(before: string, after: string): DiffPart[] {
  const a = tokenize(before);
  const b = tokenize(after);

  // LCS length table.
  const table: number[][] = Array.from({ length: a.length + 1 }, () =>
    new Array<number>(b.length + 1).fill(0),
  );
  for (let i = a.length - 1; i >= 0; i--) {
    for (let j = b.length - 1; j >= 0; j--) {
      table[i][j] = a[i] === b[j] ? table[i + 1][j + 1] + 1 : Math.max(table[i + 1][j], table[i][j + 1]);
    }
  }

  const parts: DiffPart[] = [];
  let i = 0;
  let j = 0;
  while (i < a.length && j < b.length) {
    if (a[i] === b[j]) {
      push(parts, "same", a[i]);
      i++;
      j++;
    } else if (table[i + 1][j] >= table[i][j + 1]) {
      push(parts, "removed", a[i]);
      i++;
    } else {
      push(parts, "added", b[j]);
      j++;
    }
  }
  while (i < a.length) {
    push(parts, "removed", a[i++]);
  }
  while (j < b.length) {
    push(parts, "added", b[j++]);
  }
  return parts;
}

function tokenize(text: string): string[] {
  // Words with their trailing whitespace, and newlines as their own tokens, so the rendered
  // diff keeps the line structure.
  return text.split(/(\n)/).flatMap((chunk) => (chunk === "\n" ? ["\n"] : chunk.match(/\S+\s*/g) ?? []));
}

function push(parts: DiffPart[], kind: DiffPart["kind"], text: string): void {
  const last = parts[parts.length - 1];
  if (last && last.kind === kind) {
    last.text += text;
  } else {
    parts.push({ kind, text });
  }
}
